import { useMemo, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-toastify';

import { Modal } from '../../../../components/Modal/Modal';
import { useBulkInvoice } from '../../api';
import { orderIdOf, distinctOrders } from '../grain';
import { summarizeBulk } from '../summarize';
import type { BulkAction, BulkActionBarContext } from '../types';

const MAX_BULK_INVOICE_ORDERS = 15;

// Invoice the selected loads in QuickBooks. Invoicing is per-ORDER, so the loads
// are grouped to distinct orders first (one QBO invoice per order).
function InvoiceDialog({ ctx, onClose }: { ctx: BulkActionBarContext; onClose: () => void }) {
  const { rows, count } = ctx;
  const bulkInvoice = useBulkInvoice();
  const isApplying = bulkInvoice.isPending;

  const [mode, setMode] = useState<'CREATE' | 'CREATE_AND_SEND'>('CREATE');

  const orders = useMemo(() => distinctOrders(rows), [rows]);
  const rowsWithoutOrder = count - rows.filter((row) => orderIdOf(row)).length;
  const overCap = orders.length > MAX_BULK_INVOICE_ORDERS;

  const apply = async () => {
    if (orders.length === 0 || overCap) return;

    try {
      const res = await bulkInvoice.mutateAsync({ orderIds: orders.map((o) => o.id), mode });

      const labelById = new Map(orders.map((o) => [o.id, o.label]));
      const failed = res.failed.map((f) => ({
        item: { label: labelById.get(f.orderId) ?? `order ${f.orderId.slice(-6)}` },
        error: f.message || f.error,
      }));

      const summary = summarizeBulk(res.succeeded, failed, orders.length, 'invoiced', 'order');
      if (summary.tone === 'success') toast.success(summary.message);
      else toast.warn(summary.message);
      if (summary.clearSelection) ctx.onClearSelection();
      onClose();
    } catch (e: any) {
      const status = e?.response?.status;
      // A timeout/gateway error returns no partial-success body. Invoicing is
      // idempotent, so re-running safely skips the orders already created.
      if (!e?.response || status === 502 || status === 504) {
        toast.warn(
          'Timed out before finishing. Some invoices may have been created — re-run Invoice to finish the rest.'
        );
      } else {
        toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Failed to invoice.');
      }
    }
  };

  return (
    <Modal
      isOpen
      onClose={isApplying ? () => {} : onClose}
      title={`Invoice ${orders.length} order${orders.length === 1 ? '' : 's'}`}
      size="sm"
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={isApplying}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={apply}
            disabled={isApplying || orders.length === 0 || overCap}
          >
            {isApplying ? 'Invoicing…' : `Invoice ${orders.length}`}
          </button>
        </>
      }
    >
      <p>
        The {count} selected load{count === 1 ? '' : 's'} span <strong>{orders.length}</strong>{' '}
        order{orders.length === 1 ? '' : 's'}. One QuickBooks invoice is created per order.
      </p>

      {overCap && (
        <div className="bulk-confirm__warning">
          That&apos;s more than {MAX_BULK_INVOICE_ORDERS} orders — narrow the selection and try
          again.
        </div>
      )}

      {rowsWithoutOrder > 0 && (
        <div className="bulk-confirm__warning">
          {rowsWithoutOrder} selected load{rowsWithoutOrder === 1 ? '' : 's'} have no order and are
          ignored.
        </div>
      )}

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
        <input
          type="checkbox"
          checked={mode === 'CREATE_AND_SEND'}
          onChange={(e) => setMode(e.target.checked ? 'CREATE_AND_SEND' : 'CREATE')}
          disabled={isApplying}
        />
        Also send each invoice to the customer
      </label>
    </Modal>
  );
}

function InvoiceTrigger({ ctx }: { ctx: BulkActionBarContext }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
        <FileSpreadsheet size={14} />
        Invoice
      </button>

      {open && <InvoiceDialog ctx={ctx} onClose={() => setOpen(false)} />}
    </>
  );
}

export const InvoiceAction: BulkAction = {
  id: 'invoice',
  isAvailable: (ctx) => ctx.rows.some((row) => orderIdOf(row)),
  Component: InvoiceTrigger,
};
